import { IconButton } from 'design-system';
import { ReactNode, useCallback, useState } from 'react';
import { MdLink } from 'react-icons/md';
import { Field, FieldGroupFragment as FieldGroup, FormType, InputType, LogicalOperator } from 'generated/graphql';

const LINK_TO_PARENT_FIELD_LABEL = 'Link to catalog product';
const PRODUCT_FIELD_LABEL = 'Product';
const DESTINATION_FIELD_LABELS: ReadonlySet<string> = new Set(['Destination', 'Allocate to shipment']);
const PROCUREMENT_DESTINATION_VALUES = new Set(['32000', 'procurement']);

const findProductField = (groupFields: Field[]): Field | undefined =>
  groupFields.find((f) => f.type === InputType.Autocomplete && f.label === PRODUCT_FIELD_LABEL);

interface RowLinkingState {
  rowId: string;
  productLabelOverride: string;
  linkSelectorVisible: boolean;
  isButtonDisabled: boolean;
}

export interface InboundProductLinkingDecorator {
  decorateField: (field: Field, fieldGroup: FieldGroup) => Field | null;
  renderRowActions: (fieldGroup: FieldGroup) => ReactNode;
}

export function useInboundProductLinking(
  formType: FormType,
  formFields: Field[],
  isLoading: boolean,
): InboundProductLinkingDecorator | undefined {
  const [linkVisibilityByRow, setLinkVisibilityByRow] = useState<Map<string, boolean>>(new Map());
  const setLinkVisibility = useCallback((rowId: string, visible: boolean) => {
    setLinkVisibilityByRow((prev) => {
      const next = new Map(prev);
      next.set(rowId, visible);
      return next;
    });
  }, []);

  const destinationField = formFields.find((f) => DESTINATION_FIELD_LABELS.has(f.label));
  const isProcurementDestination = PROCUREMENT_DESTINATION_VALUES.has(destinationField?.value ?? '');

  const isMoveOutbound = formType === FormType.MoveOutboundShipmentInventory;

  const stateForMoveOutboundRow = (fieldGroup: FieldGroup): RowLinkingState | null => {
    if (!isProcurementDestination) return null;
    const rowId = fieldGroup.fields[0]?.fieldGroupID ?? '';
    if (!rowId) return null;
    const linkField = fieldGroup.fields.find((f) => f.label === LINK_TO_PARENT_FIELD_LABEL);
    if (!linkField) return null;

    const userOverride = linkVisibilityByRow.get(rowId);
    const linkSelectorVisible = userOverride !== undefined ? userOverride : true;
    return {
      rowId,
      productLabelOverride: '',
      linkSelectorVisible,
      isButtonDisabled: false,
    };
  };

  const stateForInboundRow = (fieldGroup: FieldGroup): RowLinkingState | null => {
    if (!isProcurementDestination) return null;
    const rowId = fieldGroup.fields[0]?.fieldGroupID ?? '';
    if (!rowId) return null;
    const productField = findProductField(fieldGroup.fields);
    const productValue = productField?.value ?? '';
    if (!productValue) return null;

    const pickedOption = productField?.selectOptions.find((o) => o.value === productValue);
    const committedParentName = pickedOption?.description ?? '';

    const linkFieldValue = fieldGroup.fields.find((f) => f.label === LINK_TO_PARENT_FIELD_LABEL)?.value ?? '';
    const isCommittedLinked = Boolean(committedParentName);
    const isStagedLinked = isCommittedLinked || Boolean(linkFieldValue);

    const userOverride = linkVisibilityByRow.get(rowId);
    const linkSelectorVisible = userOverride !== undefined ? userOverride : !isCommittedLinked;

    return {
      rowId,
      productLabelOverride: committedParentName ? `Linked to: ${committedParentName}` : 'Child product',
      linkSelectorVisible,
      isButtonDisabled: linkSelectorVisible && !isStagedLinked,
    };
  };

  const stateForRow = (fieldGroup: FieldGroup): RowLinkingState | null =>
    isMoveOutbound ? stateForMoveOutboundRow(fieldGroup) : stateForInboundRow(fieldGroup);

  const decorateField = (field: Field, fieldGroup: FieldGroup): Field | null => {
    const state = stateForRow(fieldGroup);
    if (!state) return field;
    if (!isMoveOutbound && field.label === PRODUCT_FIELD_LABEL && field.type === InputType.Autocomplete) {
      return { ...field, label: state.productLabelOverride };
    }
    if (field.label === LINK_TO_PARENT_FIELD_LABEL) {
      if (!state.linkSelectorVisible) return null;
      return {
        ...field,
        showIf: null,
        showIfs: { ifs: [], operator: LogicalOperator.And },
      };
    }
    return field;
  };

  const renderRowActions = (fieldGroup: FieldGroup): ReactNode => {
    const state = stateForRow(fieldGroup);
    if (!state) return null;
    const closedLabel = 'Change or assign catalog parent';
    const openLabel = 'Hide catalog parent selector';
    return (
      <IconButton
        key="relink-button"
        disabled={isLoading || state.isButtonDisabled}
        size="small"
        aria-label={state.linkSelectorVisible ? openLabel : closedLabel}
        title={
          state.isButtonDisabled ? 'Pick a catalog parent first' : state.linkSelectorVisible ? openLabel : closedLabel
        }
        color={state.linkSelectorVisible ? 'primary' : 'inherit'}
        onClick={() => {
          if (state.isButtonDisabled) return;
          setLinkVisibility(state.rowId, !state.linkSelectorVisible);
        }}
        sx={(theme) => ({
          flexShrink: 0,
          flexGrow: 0,
          width: theme.spacing(4),
          height: theme.spacing(4),
          minWidth: theme.spacing(4),
        })}
      >
        <MdLink size={20} />
      </IconButton>
    );
  };

  return { decorateField, renderRowActions };
}
